import { toDisplayWeight } from "./units";
import { EXERCISE_LIBRARY } from "../constants";

const getExerciseMeta = (exerciseName) => {
  const name = (exerciseName || "").toLowerCase();
  const match = EXERCISE_LIBRARY.find(e => name.includes(e.name.toLowerCase()));
  return match || { type: "machine" };
};

const getIncrementFromType = (type, weight, unit) => {
  if (type === "barbell") {
    return unit === "kg" ? 2.5 : 5;
  }
  if (type === "dumbbell") {
    if (unit === "kg") return weight < 10 ? 1 : 2.5;
    return weight < 20 ? 2 : 5;
  }
  return unit === "kg" ? 2.5 : 5;
};

export const roundToIncrement = (value, increment) => {
  if (!increment || increment <= 0) return value;
  return Math.round(value / increment) * increment;
};

// Extracts a millisecond timestamp from a sessionKey like "session_1745555000000".
// Returns null if no 10+-digit number is found (e.g. bare date strings).
const extractTimestamp = (key) => {
  if (key == null) return null;
  const match = String(key).match(/(\d{10,})/);
  return match ? Number(match[1]) : null;
};

export const getSortedHistory = (history) => {
  if (!history || history.length === 0) return [];
  return [...history].sort((a, b) => {
    const keyA = a.sessionKey ?? a.sessionId;
    const keyB = b.sessionKey ?? b.sessionId;
    const tsA  = extractTimestamp(keyA);
    const tsB  = extractTimestamp(keyB);
    if (tsA !== null && tsB !== null) return tsA - tsB;
    const dateA = a.date ? new Date(a.date + "T12:00:00").getTime() : 0;
    const dateB = b.date ? new Date(b.date + "T12:00:00").getTime() : 0;
    return dateA - dateB;
  });
};

const buildHistory = (workouts, { exerciseId, exerciseName }) => {
  const lowerName = (exerciseName || "").toLowerCase();
  const matches = w => {
    if (w.type !== "strength") return false;
    if (w.isDropset === true) return false;
    if (w.isMyoRep  === true) return false;
    if (exerciseId && w.exerciseId) return w.exerciseId === exerciseId;
    return w.name?.toLowerCase() === lowerName;
  };
  const grouped = workouts
    .filter(matches)
    .reduce((acc, w) => {
      const key = w.sessionId || w.date;
      if (!acc[key]) acc[key] = { date: w.date, sessionId: key, sets: [] };
      acc[key].sets.push({ weight: w.weight, reps: w.reps });
      return acc;
    }, {});
  return getSortedHistory(
    Object.values(grouped).map(s => {
      const best = s.sets.reduce((b, set) => {
        const w1rm = set.weight > 0 ? set.weight * (1 + set.reps / 30) : set.reps;
        return w1rm > b.w1rm ? { weight: set.weight, reps: set.reps, w1rm } : b;
      }, { weight: 0, reps: 0, w1rm: 0 });
      return { date: s.date, sessionId: s.sessionId, sets: s.sets, best };
    })
  );
};

export const getProgressionSuggestion = ({ workouts, exerciseId, exerciseName, repRange, unit, increment, progressionDirection = "up" }) => {
  if (!workouts || (!exerciseId && !exerciseName) || !repRange) return null;

  const history = buildHistory(workouts, { exerciseId, exerciseName });
  if (history.length === 0) return null;

  const last        = history[history.length - 1];
  const maxWeight   = last.sets.reduce((m, s) => s.weight > m ? s.weight : m, 0);
  const workingSets = last.sets.filter(s => s.weight === maxWeight);
  if (workingSets.length === 0) return null;

  const isBodyweight  = maxWeight === 0;
  const lastDispW     = toDisplayWeight(maxWeight, unit);
  const meta          = exerciseId ? (EXERCISE_LIBRARY.find(e => e.id === exerciseId) || getExerciseMeta(exerciseName)) : getExerciseMeta(exerciseName);
  const equipmentType = meta.type;
  const isMachine     = equipmentType === "machine";
  const incrementUsed = increment ?? getIncrementFromType(equipmentType, lastDispW, unit);
  const isDown        = progressionDirection === "down";
  const unitLbl       = unit === "kg" ? "kg" : "lbs";

  // For "down" exercises, harder = less logged weight (assistance). So progression subtracts; regression adds.
  const harder = (w) => Math.max(0, +(isDown ? w - incrementUsed : w + incrementUsed).toFixed(2));
  const easier = (w) => Math.max(0, +(isDown ? w + incrementUsed : w - incrementUsed).toFixed(2));

  // Plateau: w1rm flat or non-increasing across the last 3 sessions
  if (!isBodyweight && history.length >= 3) {
    const [a, b, c] = history.slice(-3).map(s => s.best.w1rm);
    if (b <= a && c <= b) {
      if (isMachine) {
        return {
          type: "deload",
          mode: "suggest",
          message: isDown ? "Deload — use more assistance" : "Deload — try a lighter setting",
          incrementUsed,
          progressionDirection,
        };
      }
      const deloadW = isDown
        ? Math.max(0, roundToIncrement(lastDispW * 1.25, incrementUsed))
        : Math.max(0, roundToIncrement(lastDispW * 0.8,  incrementUsed));
      return {
        type: "deload",
        mode: "exact",
        weight: deloadW,
        repSuggestion: "Focus on clean reps",
        incrementUsed,
        progressionDirection,
      };
    }
  }

  const totalSets    = workingSets.length;
  const setsAtMax    = workingSets.filter(s => s.reps >= repRange.max).length;
  const setsBelowMin = workingSets.filter(s => s.reps < repRange.min).length;

  if (!isBodyweight && setsAtMax > 0 && setsAtMax >= totalSets - 1) {
    if (isMachine) {
      return {
        type: "progress",
        mode: "suggest",
        message: isDown ? `Reduce assistance by ${incrementUsed} ${unitLbl}` : "Increase weight next session",
        incrementUsed,
        progressionDirection,
      };
    }
    return {
      type: "progress",
      mode: "exact",
      weight: harder(lastDispW),
      repSuggestion: isDown ? "Reduce assistance" : null,
      incrementUsed,
      progressionDirection,
    };
  }

  if (!isBodyweight && setsBelowMin >= 2) {
    if (isMachine) {
      return {
        type: "decrease",
        mode: "suggest",
        message: isDown ? `Add ${incrementUsed} ${unitLbl} more assistance` : "Reduce weight slightly",
        incrementUsed,
        progressionDirection,
      };
    }
    return {
      type: "decrease",
      mode: "exact",
      weight: easier(lastDispW),
      repSuggestion: isDown ? "Add more assistance" : null,
      incrementUsed,
      progressionDirection,
    };
  }

  const avgReps     = workingSets.reduce((s, x) => s + x.reps, 0) / workingSets.length;
  const maintainMsg = avgReps < repRange.max ? "Increase reps this set" : "Maintain and stabilise";
  if (isMachine) {
    return {
      type: "maintain",
      mode: "suggest",
      message: maintainMsg,
      incrementUsed,
      progressionDirection,
    };
  }
  return {
    type: "maintain",
    mode: "exact",
    weight: lastDispW,
    repSuggestion: maintainMsg,
    incrementUsed,
    progressionDirection,
  };
};
